import React, { useCallback, useEffect, useState, useRef } from 'react';
import { getTypeformUrl } from '../utils/cookies';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { motion } from 'framer-motion';
export const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoCardRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const [showControls, setShowControls] = useState(true);
  const controlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (!hasStarted) video.currentTime = 0;
      video.play();
      setIsPlaying(true);
      setHasStarted(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [hasStarted]);
  const handleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);
  const handleFullscreen = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.requestFullscreen) video.requestFullscreen();
  }, []);
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * video.duration;
  }, []);
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    if (isPlaying) {
      controlsTimeout.current = setTimeout(() => setShowControls(false), 2500);
    }
  }, [isPlaying]);
  const handleWatchVideo = useCallback(() => {
    const video = videoRef.current;
    const card = videoCardRef.current;
    if (!video || !card) return;
    card.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    setTimeout(() => {
      video.currentTime = 0;
      video.muted = false;
      video.play().then(() => {
        setIsPlaying(true);
        setHasStarted(true);
        setIsMuted(false);
      }).catch(() => {
        video.muted = true;
        video.play().then(() => {
          setIsPlaying(true);
          setHasStarted(true);
          setIsMuted(true);
        }).catch(() => {});
      });
    }, 600);
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onLoadedMetadata = () => {
      video.currentTime = 1;
      setDuration(formatTime(video.duration));
    };
    const onTimeUpdate = () => {
      if (video.duration) {
        setProgress(video.currentTime / video.duration * 100);
        setCurrentTime(formatTime(video.currentTime));
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, []);
  return <>
      <style data-id="element-345">{`
        @keyframes goldGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(216,163,77,0.2), 0 0 60px rgba(216,163,77,0.1), 0 30px 80px rgba(0,0,0,0.4); }
          50% { box-shadow: 0 0 50px rgba(216,163,77,0.5), 0 0 100px rgba(216,163,77,0.25), 0 0 150px rgba(216,163,77,0.1), 0 30px 80px rgba(0,0,0,0.4); }
        }
        @keyframes cornerDraw {
          0% { stroke-dashoffset: 100; opacity: 0.2; }
          50% { stroke-dashoffset: 0; opacity: 1; }
          100% { stroke-dashoffset: 100; opacity: 0.2; }
        }
        @keyframes borderTrace {
          0% { stroke-dashoffset: 2000; }
          100% { stroke-dashoffset: 0; }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* ── GREEN HERO ── */}
      <section className="relative bg-[#1a2e1c] text-white overflow-hidden pt-24 pb-56 md:pt-28 md:pb-72 md:h-[650px]" data-id="element-346">
        <div className="absolute inset-0 pointer-events-none" data-id="element-347">
          <img src="https://cdn.magicpatterns.com/uploads/pUT2DaNjCU9Rh2RWu17Qw5/bullseye-gradient.png" alt="" className="w-full h-full object-cover" data-id="element-348" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10" data-id="element-349">
          <div className="max-w-6xl mx-auto text-center" data-id="element-350">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-[34px] md:text-[3.55rem] font-display font-bold text-white leading-[1.1] mb-4 md:mb-7 tracking-tight" data-id="element-351">
              Tired of Returns That Barely Beat Inflation?{' '}
              <br className="hidden md:block" data-id="element-352" />
              Dubai Properties Deliver{' '}
              <span className="text-[#d8a34d]" data-id="element-353">15–20% Growth</span> + Rental
              Yields.
            </motion.h1>

            {/* Watch the Video CTA with curved arrows */}
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.6
          }} className="flex items-center justify-center gap-3 md:gap-5 mb-4 md:mb-6" data-id="element-354">
              {/* Left arrow — pointing right toward button */}
              <motion.img src="https://cdn.magicpatterns.com/uploads/trEcrNzAbFDPnTA8HYjLRY/Vector.svg" alt="" className="w-14 h-10 md:w-20 md:h-14 hidden md:block !-scale-x-100" style={{
              transform: 'scaleX(-1)',
              filter: 'brightness(0) saturate(100%) invert(73%) sepia(50%) saturate(500%) hue-rotate(5deg) brightness(95%) contrast(90%)'
            }} animate={{
              x: [0, 6, 0]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-355" />

              {/* CTA pill button */}
              <motion.button onClick={handleWatchVideo} animate={{
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative overflow-hidden bg-[#d8a34d] text-[#1a2e1c] font-display font-extrabold text-base md:text-xl lg:text-2xl tracking-wide uppercase px-8 py-3 md:px-12 md:py-4 rounded-full cursor-pointer hover:brightness-110 transition-all" style={{
              boxShadow: '0 0 30px rgba(216,163,77,0.5), 0 0 60px rgba(216,163,77,0.2)'
            }} data-id="element-356">
                <span className="relative z-10 flex items-center gap-2.5" data-id="element-357">
                  <Play className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" data-id="element-358" />
                  Watch the Video Now!!
                </span>
                {/* Shimmer on pill */}
                <div className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.25) 55%, transparent 60%)',
                animation: 'shimmer 3s ease-in-out infinite'
              }} data-id="element-359" />
              </motion.button>

              {/* Right arrow — pointing left toward button */}
              <motion.img src="https://cdn.magicpatterns.com/uploads/trEcrNzAbFDPnTA8HYjLRY/Vector.svg" alt="" className="w-14 h-10 md:w-20 md:h-14 hidden md:block" style={{
              transform: 'scaleX(-1)',
              filter: 'brightness(0) saturate(100%) invert(73%) sepia(50%) saturate(500%) hue-rotate(5deg) brightness(95%) contrast(90%)'
            }} animate={{
              x: [0, -6, 0]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-360" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHITE / VIDEO SECTION ── */}
      <section className="relative pb-4 md:pb-6" data-id="element-361">
        <div className="container mx-auto px-2 md:px-8" data-id="element-362">
          <div className="max-w-6xl mx-auto text-center" data-id="element-363">
            <div className="-mt-48 xl:-mt-[14rem] mb-12 md:mb- relative z-10" data-id="element-364">
              {/* ── VIDEO CARD ── */}
              <div className="relative" data-id="element-365">
                {/* Striking corner brackets with glow — only before play */}
                {!hasStarted && <>
                    {/* Top-left */}
                    <motion.svg initial={{
                  opacity: 0,
                  scale: 0.5
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 0.5
                }} className="absolute -top-5 -left-5 w-16 h-16 md:w-24 md:h-24 z-30 pointer-events-none" viewBox="0 0 60 60" fill="none" style={{
                  filter: 'drop-shadow(0 0 8px rgba(216,163,77,0.7))'
                }} data-id="element-366">
                      <path d="M5 45 L5 5 L45 5" stroke="#d8a34d" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="100" style={{
                    animation: 'cornerDraw 2.5s ease-in-out infinite'
                  }} data-id="element-367" />
                    </motion.svg>
                    {/* Top-right */}
                    <motion.svg initial={{
                  opacity: 0,
                  scale: 0.5
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 0.65
                }} className="absolute -top-5 -right-5 w-16 h-16 md:w-24 md:h-24 z-30 pointer-events-none" viewBox="0 0 60 60" fill="none" style={{
                  filter: 'drop-shadow(0 0 8px rgba(216,163,77,0.7))'
                }} data-id="element-368">
                      <path d="M55 45 L55 5 L15 5" stroke="#d8a34d" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="100" style={{
                    animation: 'cornerDraw 2.5s 0.25s ease-in-out infinite'
                  }} data-id="element-369" />
                    </motion.svg>
                    {/* Bottom-left */}
                    <motion.svg initial={{
                  opacity: 0,
                  scale: 0.5
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 0.8
                }} className="absolute -bottom-5 -left-5 w-16 h-16 md:w-24 md:h-24 z-30 pointer-events-none" viewBox="0 0 60 60" fill="none" style={{
                  filter: 'drop-shadow(0 0 8px rgba(216,163,77,0.7))'
                }} data-id="element-370">
                      <path d="M5 15 L5 55 L45 55" stroke="#d8a34d" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="100" style={{
                    animation: 'cornerDraw 2.5s 0.5s ease-in-out infinite'
                  }} data-id="element-371" />
                    </motion.svg>
                    {/* Bottom-right */}
                    <motion.svg initial={{
                  opacity: 0,
                  scale: 0.5
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.6,
                  delay: 0.95
                }} className="absolute -bottom-5 -right-5 w-16 h-16 md:w-24 md:h-24 z-30 pointer-events-none" viewBox="0 0 60 60" fill="none" style={{
                  filter: 'drop-shadow(0 0 8px rgba(216,163,77,0.7))'
                }} data-id="element-372">
                      <path d="M55 15 L55 55 L15 55" stroke="#d8a34d" strokeWidth="3.5" strokeLinecap="round" strokeDasharray="100" style={{
                    animation: 'cornerDraw 2.5s 0.75s ease-in-out infinite'
                  }} data-id="element-373" />
                    </motion.svg>
                  </>}

                <div ref={videoCardRef} className="relative rounded-2xl overflow-hidden border border-gray-200 bg-white p-1.5 md:p-2" style={!hasStarted ? {
                animation: 'goldGlow 2.5s ease-in-out infinite'
              } : {
                boxShadow: '0 30px 80px rgba(0,0,0,0.4)'
              }} data-id="element-374">
                  {/* Shimmer sweep across card before play */}
                  {!hasStarted && <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-2xl" data-id="element-375">
                      <div className="absolute inset-0 w-full h-full" style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(216,163,77,0.08) 45%, rgba(216,163,77,0.15) 50%, rgba(216,163,77,0.08) 55%, transparent 60%)',
                    animation: 'shimmer 3.5s ease-in-out infinite'
                  }} data-id="element-376" />
                    </div>}

                  <div className="rounded-xl overflow-hidden bg-black relative group cursor-pointer" style={{
                  paddingTop: '56.25%'
                }} onMouseMove={handleMouseMove} onMouseLeave={() => {
                  if (isPlaying) {
                    controlsTimeout.current = setTimeout(() => setShowControls(false), 1500);
                  }
                }} data-id="element-377">
                    <video ref={videoRef} src="https://ikigaire.ae/wp-content/uploads/2026/03/Video-01-Long_06-3.mp4" playsInline preload="metadata" onClick={handlePlayPause} className="absolute inset-0 w-full h-full object-cover" data-id="element-378" />

                    {/* Striking play overlay with dramatic pulsing rings */}
                    {!hasStarted && <div className="absolute inset-0 flex items-center justify-center bg-black/25 z-10" onClick={handlePlayPause} data-id="element-379">
                        <div className="relative" data-id="element-380">
                          {/* Gold burst — filled expanding circle */}
                          <motion.div animate={{
                        scale: [1, 2.2],
                        opacity: [0.3, 0]
                      }} transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'easeOut'
                      }} className="absolute inset-[-12px] rounded-full bg-[#d8a34d]/20" data-id="element-381" />
                          {/* Ring 1 — fast, tight */}
                          <motion.div animate={{
                        scale: [1, 1.7],
                        opacity: [0.6, 0]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeOut'
                      }} className="absolute inset-[-4px] rounded-full border-[3px] border-[#d8a34d]" style={{
                        filter: 'drop-shadow(0 0 6px rgba(216,163,77,0.5))'
                      }} data-id="element-382" />
                          {/* Ring 2 — medium */}
                          <motion.div animate={{
                        scale: [1, 2],
                        opacity: [0.4, 0]
                      }} transition={{
                        duration: 2,
                        delay: 0.5,
                        repeat: Infinity,
                        ease: 'easeOut'
                      }} className="absolute inset-[-4px] rounded-full border-2 border-[#d8a34d]" style={{
                        filter: 'drop-shadow(0 0 4px rgba(216,163,77,0.4))'
                      }} data-id="element-383" />
                          {/* Ring 3 — wide, slow */}
                          <motion.div animate={{
                        scale: [1, 2.5],
                        opacity: [0.2, 0]
                      }} transition={{
                        duration: 2.5,
                        delay: 1,
                        repeat: Infinity,
                        ease: 'easeOut'
                      }} className="absolute inset-[-4px] rounded-full border border-[#d8a34d]/60" data-id="element-384" />
                          {/* Play button */}
                          <motion.div animate={{
                        scale: [1, 1.05, 1]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }} className="relative w-16 h-16 md:w-28 md:h-28 rounded-full bg-[#d8a34d] flex items-center justify-center border-4 border-white/30 cursor-pointer" style={{
                        boxShadow: '0 0 50px rgba(216,163,77,0.7), 0 0 100px rgba(216,163,77,0.3)'
                      }} data-id="element-385">
                            <Play className="w-7 h-7 md:w-11 md:h-11 text-white ml-1" fill="white" data-id="element-386" />
                          </motion.div>
                        </div>
                      </div>}

                    {/* Custom controls */}
                    {hasStarted && <div className={`absolute bottom-0 left-0 right-0 z-20 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`} data-id="element-387">
                        <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-10 pb-3 px-4" data-id="element-388">
                          <div ref={progressRef} className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 group/progress hover:h-2.5 transition-all" onClick={handleProgressClick} data-id="element-389">
                            <div className="h-full bg-[#d8a34d] rounded-full relative transition-all" style={{
                          width: `${progress}%`
                        }} data-id="element-390">
                              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/progress:opacity-100 transition-opacity" data-id="element-391" />
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-3" data-id="element-392">
                            <div className="flex items-center gap-3" data-id="element-393">
                              <button onClick={e => {
                            e.stopPropagation();
                            handlePlayPause();
                          }} className="text-white hover:text-[#d8a34d] transition-colors" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-394">
                                {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" data-id="element-395" /> : <Play className="w-5 h-5" fill="currentColor" data-id="element-396" />}
                              </button>
                              <button onClick={e => {
                            e.stopPropagation();
                            handleMute();
                          }} className="text-white hover:text-[#d8a34d] transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-397">
                                {isMuted ? <VolumeX className="w-5 h-5" data-id="element-398" /> : <Volume2 className="w-5 h-5" data-id="element-399" />}
                              </button>
                              <span className="text-white/70 text-xs font-medium tabular-nums" data-id="element-400">
                                {currentTime} / {duration}
                              </span>
                            </div>
                            <button onClick={e => {
                          e.stopPropagation();
                          handleFullscreen();
                        }} className="text-white hover:text-[#d8a34d] transition-colors" aria-label="Fullscreen" data-id="element-401">
                              <Maximize className="w-4.5 h-4.5" data-id="element-402" />
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Primary CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-403">
              <motion.a href="#" onClick={e => {
              e.preventDefault();
              window.location.href = getTypeformUrl();
            }} animate={{
              scale: [1, 1.03, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative overflow-hidden block w-full py-6 md:py-7 rounded-2xl font-extrabold text-xl md:text-2xl lg:text-3xl tracking-wide uppercase text-white cursor-pointer text-center" style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #162d4a 40%, #0f1f35 100%)',
              boxShadow: '0 0 30px rgba(30,58,95,0.5), 0 0 60px rgba(30,58,95,0.2), 0 8px 30px rgba(0,0,0,0.3)'
            }} data-id="element-404">
                <span className="relative z-10" data-id="element-405">Book Your Consultation!</span>
                <div className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.2) 55%, transparent 60%)',
                animation: 'shimmer 3s ease-in-out infinite'
              }} data-id="element-406" />
              </motion.a>
              <p className="mt-3 text-sm text-gray-500 font-medium" data-id="element-407">
                Get access to a Free Dubai Investment Guide & Recommended
                Projects{' '}
                <span className="text-[#d8a34d] font-bold" data-id="element-408">
                  &gt;&gt;&gt;&gt;
                </span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
};